import { getJson, getList, sendJson, type PaginationMeta } from '@/lib/api';
import type { StatsInterval } from '@/lib/leads';

/**
 * The Opportunity analytics mirror — typed fetchers over
 * /api/v3/opportunities. Shapes mirror apps/api's OpportunityRecord /
 * EventStats / OpportunitySyncStatus.
 */

export const EVENT_SEGMENT = {
  program: 'program',
  region: 'region',
  salesperson: 'salesperson',
} as const;
export type EventSegment = (typeof EVENT_SEGMENT)[keyof typeof EVENT_SEGMENT];

export const OPPORTUNITY_OUTCOME = {
  signed: 'signed',
  cancelled: 'cancelled',
  open: 'open',
  lost: 'lost',
  unqualified: 'unqualified',
} as const;
export type OpportunityOutcome = (typeof OPPORTUNITY_OUTCOME)[keyof typeof OPPORTUNITY_OUTCOME];

export interface EventStatsPoint {
  readonly period: string;
  readonly events: number;
  readonly value: number;
  readonly participants: number;
}

export interface EventBreakdownRow {
  readonly key: string;
  readonly events: number;
  readonly participants: number;
  readonly value: number;
}

export interface EventStats {
  readonly range: {
    readonly from: string;
    readonly to: string;
    readonly interval: StatsInterval;
    readonly leadIn: number;
  };
  readonly totals: {
    readonly events: number;
    readonly value: number;
    readonly participants: number;
    readonly averageParticipants: number;
    readonly cancelled: number;
    readonly cancelledValue: number;
    readonly previousEvents: number;
    readonly changePct: number | null;
    readonly medianLeadTimeDays: number | null;
  };
  readonly series: readonly EventStatsPoint[];
  readonly segments: ReadonlyArray<{ key: string; values: readonly number[]; total: number }> | null;
  readonly yoy: readonly number[] | null;
  readonly byProgram: readonly EventBreakdownRow[];
  readonly byState: readonly EventBreakdownRow[];
  readonly leadTimes: ReadonlyArray<{ key: string; events: number }>;
}

export interface Opportunity {
  readonly id: string;
  readonly salesforceId: string;
  readonly name: string;
  readonly accountName: string;
  readonly stageName: string;
  readonly outcome: OpportunityOutcome;
  readonly signedAt: string | null;
  readonly closeDate: string | null;
  readonly eventDate: string | null;
  readonly totalCost: number | null;
  readonly reasonLost: string;
  readonly sourceChannel: string;
  readonly salesperson: string;
  readonly programName: string;
  readonly eventCity: string;
  readonly eventState: string;
  readonly participants: number | null;
  readonly invoiceNumber: string;
  readonly sfCreatedAt: string;
}

/** Sortable columns on GET /opportunities (mirrors apps/api OPPORTUNITY_SORT_FIELDS). */
export const OPPORTUNITY_SORT_KEYS = [
  'signedAt',
  'createdAt',
  'eventDate',
  'account',
  'value',
  'salesperson',
  'stage',
] as const;

export interface OpportunitySyncStatus {
  readonly opportunities: number;
  readonly pipeline: { readonly count: number; readonly value: number };
  readonly latestSystemModstamp: string | null;
  readonly lastRun: {
    readonly id: string;
    readonly finishedAt: string | null;
    readonly error: string | null;
  } | null;
}

/** Where an opportunity opens in Salesforce. */
export function salesforceOpportunityUrl(salesforceId: string): string {
  return `https://bestcorporateevents.lightning.force.com/${salesforceId}`;
}

export function listOpportunities(
  params: {
    search?: string;
    from?: string;
    to?: string;
    dateBy?: 'signed' | 'event';
    outcome?: OpportunityOutcome;
    page?: number;
    limit?: number;
    sort?: string;
    dir?: 'asc' | 'desc';
  },
  signal?: AbortSignal,
): Promise<{ items: Opportunity[]; pagination: PaginationMeta }> {
  return getList<Opportunity>('/opportunities', params, signal);
}

export function readEventStats(
  params: {
    from: string;
    to: string;
    interval: StatsInterval;
    leadIn?: number;
    segment?: EventSegment;
    yoy?: '1';
  },
  signal?: AbortSignal,
): Promise<EventStats> {
  return getJson<EventStats>('/opportunities/event-stats', params, signal);
}

export function readOpportunitySyncStatus(signal?: AbortSignal): Promise<OpportunitySyncStatus> {
  return getJson<OpportunitySyncStatus>('/opportunities/sync/status', undefined, signal);
}

export function syncOpportunitiesNow(): Promise<unknown> {
  return sendJson<unknown>('post', '/opportunities/sync');
}
